import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Menu, X, Phone, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [footerContent, setFooterContent] = useState({
    phone: "862-350-7122",
    email: "estimating@astute-builders.com",
    location: "New Jersey"
  });
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/site-content/footer`);
        if (response.data?.content) {
          setFooterContent(prev => ({
            ...prev,
            phone: response.data.content.phone || prev.phone,
            email: response.data.content.email || prev.email,
            location: response.data.content.location || prev.location
          }));
        }
      } catch (error) {
        console.error("Error fetching navbar content:", error);
      }
    };
    fetchContent();
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Projects", path: "/projects" },
    { name: "Blog", path: "/blog" },
    { name: "Contact", path: "/contact" },
    { name: "Client Portal", path: "/portal" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top bar */}
      <div className="hidden md:block bg-[#121212] border-b border-[#2A2A2A] py-2">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href={`tel:${footerContent.phone}`} className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#7ED4C6] transition-colors">
              <Phone size={14} />
              <span>{footerContent.phone}</span>
            </a>
            <a href={`mailto:${footerContent.email}`} className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#7ED4C6] transition-colors">
              <Mail size={14} />
              <span>{footerContent.email}</span>
            </a>
          </div>
          <div className="text-[#A1A1AA]">
            Serving {footerContent.location}
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <nav
        data-testid="navbar"
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? "glass" : "bg-[#0A0A0A]"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_1eeea6a6-945d-4052-ae21-011aad17a3a1/artifacts/qm929xya_fulllogo_transparent_nobuffer.png" 
                alt="Astute Builders Logo" 
                className="h-12 w-auto"
              />
              <div className="hidden sm:flex flex-col">
                <span className="font-['Oswald'] text-xl font-bold tracking-tight text-white">
                  ASTUTE BUILDERS
                </span>
                <span className="text-[9px] tracking-[0.2em] text-[#7ED4C6] uppercase">
                  Solid Foundations, Lasting Impressions
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`nav-link font-['Oswald'] text-sm tracking-wider uppercase transition-colors ${
                    isActive(link.path) ? "text-[#7ED4C6] active" : "text-white hover:text-[#7ED4C6]"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/contact"
                data-testid="nav-quote-btn"
                className="btn-primary text-sm px-6 py-3"
              >
                Get a Quote
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden text-white p-2"
              onClick={() => setIsMobileMenuOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu size={28} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <button
            className="absolute top-6 right-6 text-white"
            onClick={() => setIsMobileMenuOpen(false)}
            data-testid="close-mobile-menu"
          >
            <X size={32} />
          </button>
          <div className="flex flex-col items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`font-['Oswald'] text-2xl tracking-wider uppercase ${
                  isActive(link.path) ? "text-[#7ED4C6]" : "text-white"
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="btn-primary mt-4"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      )}
    </>
  );
};
